import { CouponArt } from "@/components/marketing/coupon/CouponArt"
import { CouponTicket } from "@/components/marketing/coupon/CouponTicket"
import { Button, Price, ProgressBar, Text } from "@/components/ui"
import { useAnimatedCurrency } from "@/hooks/use-animated-currency"
import type { BagCoupon } from "@/lib/bag-coupon"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import { TRANSITION } from "@/lib/motion"
import { AnimatePresence, motion } from "motion/react"
import { useNavigate } from "react-router-dom"
import { useCheckoutCoupons } from "../../hooks/useCheckoutCoupons"

function Missing({ cents }: { cents: number }) {
  const amount = useAnimatedCurrency(centsToReais(cents))
  return (
    <>
      <Text as="strong" weight="semibold" tabular tone="default" aria-hidden className="whitespace-nowrap">
        <motion.span>{amount}</motion.span>
      </Text>
      <span className="sr-only">{formatBRL(centsToReais(cents))}</span>
    </>
  )
}

function TitleLine({ coupon }: { coupon: BagCoupon }) {
  const { status } = coupon
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-2">
      <Text as="span" size="sm" weight="semibold" tone="default" truncate className="min-w-0">
        Cupom {coupon.code}
        {status.kind === "applied" ? null : (
          <Text as="span" weight="normal" tone="muted">
            {status.kind === "checking" ? " · conferindo…" : " · não aplicado"}
          </Text>
        )}
      </Text>
      {status.kind === "applied" ? <Price cents={status.discountCents} sign="minus" size="sm" weight="semibold" tone="success" /> : null}
    </div>
  )
}

function StateLine({ coupon }: { coupon: BagCoupon }) {
  const { status } = coupon
  if (status.kind !== "minimum" && !coupon.validity) return null
  return (
    <div className="mt-1 flex min-w-0 items-center gap-1.5 whitespace-nowrap">
      {status.kind === "minimum" && status.missingCents !== null ? (
        <Text as="span" size="sm" tone="default" className="shrink-0">
          Faltam <Missing cents={status.missingCents} />
        </Text>
      ) : null}
      {status.kind === "minimum" && coupon.minimum ? (
        <ProgressBar
          size="xs"
          value={coupon.minimum.progress}
          label={`Pedido mínimo do cupom: ${formatBRL(centsToReais(coupon.minimum.cents))}`}
          valueText={`${Math.round(coupon.minimum.progress * 100)}% do pedido mínimo`}
          className="w-16 shrink-0"
        />
      ) : null}
      {coupon.validity ? (
        <Text as="span" size="xs" tone="subtle" truncate className="min-w-0">
          {status.kind === "minimum" ? "· " : ""}
          {coupon.validity}
        </Text>
      ) : null}
    </div>
  )
}

/**
 * The order's coupon on the review step, as a small ticket: "Cupom X −R$ 5,30"
 * once applied, or "Cupom X não aplicado · faltam R$ 27,00" with a short bar
 * and "Adicionar itens" — the coupon stays in sight, iFood-style. Changing it
 * is the section's "Editar"; nothing without a coupon.
 */
export function CheckoutCouponSummary() {
  const { focus } = useCheckoutCoupons()
  const navigate = useNavigate()
  if (!focus) return null

  const single = focus.kind === "single" ? focus.coupon : null
  const waiting = single?.status.kind === "minimum"

  return (
    <CouponTicket size="sm" stub={<CouponArt size={26} muted={single !== null && single.status.kind !== "applied" && !waiting} />}>
      <div className="min-w-0 py-2 pl-3 pr-3" aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={single ? `${single.code}-${single.status.kind}` : "group"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={TRANSITION.fade}>
            {single ? (
              <>
                <TitleLine coupon={single} />
                <StateLine coupon={single} />
              </>
            ) : focus.kind === "group" ? (
              <div className="flex min-w-0 items-baseline justify-between gap-2">
                <Text as="span" size="sm" weight="semibold" tone="default">
                  {focus.coupons.length} cupons
                </Text>
                <Price cents={focus.discountCents} sign="minus" size="sm" weight="semibold" tone="success" />
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>
        {waiting ? (
          <div className="-mb-1 -mr-2 flex justify-end">
            <Button variant="ghost" tone="neutral" size="xs" weight="semibold" onClick={() => navigate("/")}>
              <Text as="span" size="xs" tone="primary">
                Adicionar itens
              </Text>
            </Button>
          </div>
        ) : null}
      </div>
    </CouponTicket>
  )
}
